import dayjs, { Dayjs } from "dayjs";

export const maxExperienceDaysTotal = 90;

/** Único vínculo de contrato por prazo determinado no formulário */
export const celetistaContratoPrazoDeterminadoVinculo = 5;

export function isDeterminedEmploymentVinculo(tipoVinculo: unknown): boolean {
  if (tipoVinculo === undefined || tipoVinculo === null || tipoVinculo === "") {
    return false;
  }
  return Number(tipoVinculo) === celetistaContratoPrazoDeterminadoVinculo;
}

export function getDeterminedContractStartDate(
  draft: Record<string, unknown>,
): Dayjs | null {
  return parseAdmissionDate(
    draft.inicio_contrato_determinado ?? draft.data_admissao,
  );
}

/** Fim do contrato determinado: no mínimo 1 dia após o início */
export function getDeterminedContractMinEndDate(
  draft: Record<string, unknown>,
): Dayjs | undefined {
  const start = getDeterminedContractStartDate(draft);
  return start ? start.add(1, "day") : undefined;
}

export function sanitizeDeterminedContractEnd(
  draft: Record<string, unknown>,
): Record<string, unknown> {
  const minEnd = getDeterminedContractMinEndDate(draft);
  const end = parseAdmissionDate(draft.fim_contrato_determinado);
  if (minEnd && end && end.isBefore(minEnd, "day")) {
    return { ...draft, fim_contrato_determinado: "" };
  }
  return draft;
}

export function syncDeterminedContractStartFromAdmission(
  draft: Record<string, unknown>,
): Record<string, unknown> {
  if (
    !isDeterminedEmploymentVinculo(draft.tipo_vinculo) ||
    !draft.data_admissao
  ) {
    return draft;
  }
  return sanitizeDeterminedContractEnd({
    ...draft,
    inicio_contrato_determinado: draft.data_admissao,
  });
}

export function parsePositiveDays(value: unknown): number | null {
  if (value === undefined || value === null || value === "") {
    return null;
  }
  const num = Number(String(value).trim());
  if (!Number.isFinite(num) || num < 1 || !Number.isInteger(num)) {
    return null;
  }
  return num;
}

export function getExperienceDaysTotal(
  diasExperiencia: unknown,
  diasProrrogacao: unknown,
): number {
  const contractDays = parsePositiveDays(diasExperiencia) ?? 0;
  const extensionDays = parsePositiveDays(diasProrrogacao) ?? 0;
  return contractDays + extensionDays;
}

export function isExperienceDaysTotalValid(
  diasExperiencia: unknown,
  diasProrrogacao: unknown,
): boolean {
  const contractDays = parsePositiveDays(diasExperiencia);
  if (contractDays === null) {
    return false;
  }
  return (
    getExperienceDaysTotal(diasExperiencia, diasProrrogacao) <=
    maxExperienceDaysTotal
  );
}

function parseAdmissionDate(value: unknown): Dayjs | null {
  if (!value) {
    return null;
  }
  const parsed = dayjs(value as string);
  return parsed.isValid() ? parsed.startOf("day") : null;
}

export type ExperienceContractDates = {
  inicio_experiencia: string;
  fim_experiencia: string;
  fim_prorrogacao_experiencia: string | null;
};

export function formatExperienceDraftDate(value: unknown): string {
  if (!value) {
    return "";
  }
  const parsed = dayjs(value as string);
  return parsed.isValid() ? parsed.format("DD/MM/YYYY") : "";
}

export function computeExperienceContractDates(
  dataAdmissao: unknown,
  diasExperiencia: unknown,
  diasProrrogacao: unknown,
): ExperienceContractDates | null {
  const admission = parseAdmissionDate(dataAdmissao);
  const contractDays = parsePositiveDays(diasExperiencia);

  if (!admission || contractDays === null) {
    return null;
  }

  const start = admission;
  const end = start.add(contractDays, "day");
  const extensionDays = parsePositiveDays(diasProrrogacao);
  const canExtend =
    extensionDays !== null &&
    isExperienceDaysTotalValid(diasExperiencia, diasProrrogacao);
  const extensionEnd = canExtend ? end.add(extensionDays, "day") : null;

  return {
    inicio_experiencia: start.toISOString(),
    fim_experiencia: end.toISOString(),
    fim_prorrogacao_experiencia: extensionEnd?.toISOString() ?? null,
  };
}

export function hasExperienceContract(draft: Record<string, unknown>): boolean {
  return (
    String(draft.contrato_experiencia) === "1" ||
    draft.contrato_experiencia === "Possui"
  );
}

/** Col. 65 preenchida → colunas 66 e 67 obrigatórias no layout */
export function hasExperienceProrrogation(
  draft: Record<string, unknown>,
): boolean {
  if (!hasExperienceContract(draft)) {
    return false;
  }
  return parsePositiveDays(draft.dias_prorrogacao) !== null;
}

export function mergeExperienceIntoDraft(
  draft: Record<string, unknown>,
): Record<string, unknown> {
  if (!hasExperienceContract(draft)) {
    return draft;
  }

  const dates = computeExperienceContractDates(
    draft.data_admissao,
    draft.dias_experiencia,
    draft.dias_prorrogacao,
  );

  if (!dates) {
    return {
      ...draft,
      inicio_experiencia: "",
      fim_experiencia: "",
      fim_prorrogacao_experiencia: "",
    };
  }

  return {
    ...draft,
    inicio_experiencia: dates.inicio_experiencia,
    fim_experiencia: dates.fim_experiencia,
    fim_prorrogacao_experiencia: dates.fim_prorrogacao_experiencia ?? "",
  };
}
